import { facultyProps } from "@/types/faculty.type";
import { firestore } from "../config/firebase.config";
import { fromFirestore, toFirestore } from "./firestoreAdapter";
import { collection, doc, addDoc, getDocs, updateDoc, deleteDoc, query, where, serverTimestamp, getDoc, Timestamp, writeBatch } from "firebase/firestore";

const facultyCollectionName: string = "institutes/{0}/faculties";

class FacultyService {

    async getAll(instituteId: string, active: boolean = true): Promise<facultyProps[]> {
        const collectionRef = collection(firestore, facultyCollectionName.replace("{0}", instituteId));

        const q = query(collectionRef, where("active", "==", active));
        const querySnapshot = await getDocs(q);

        const faculties: facultyProps[] = querySnapshot.docs.map((doc) => {
            //return data compatible with data types specified in the tasks variable 
            return fromFirestore<facultyProps>({
                id: doc.id,
                ...doc.data()
            } as facultyProps);
        });
        // Return the first institute with the searched code
        return faculties;
    }

    async getById(instituteId: string, facultyId: string): Promise<facultyProps | null> {
        const collectionRef = collection(firestore, facultyCollectionName.replace("{0}", instituteId));

        const docRef = doc(collectionRef, facultyId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const docId = docSnap.id;
            return fromFirestore<facultyProps>({
                id: docId,
                ...docSnap.data()
            } as facultyProps);
        }
        return null;
    }

    async getByRef(ref: string): Promise<facultyProps | null> {

        const docRef = doc(firestore, ref);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const docId = docSnap.id;
            return fromFirestore<facultyProps>({
                id: docId,
                ...docSnap.data()
            } as facultyProps);
        }
        return null;
    }

    async getByName(instituteId: string, facultyName: string): Promise<facultyProps[]> {
        const collectionRef = collection(firestore, facultyCollectionName.replace("{0}", instituteId));

        const q = query(collectionRef, where("name", "==", facultyName));
        const querySnapshot = await getDocs(q);

        const institutes: facultyProps[] = querySnapshot.docs.map((doc) => {
            //return data compatible with data types specified in the tasks variable 
            return fromFirestore<facultyProps>({
                id: doc.id,
                ...doc.data()
            } as facultyProps);
        });
        // Return the first institute with the searched code
        return institutes;
    }

    async create(instituteId: string, faculty: facultyProps): Promise<string> {
        const collectionRef = collection(firestore, facultyCollectionName.replace("{0}", instituteId));
        // Add a new document with a generated id.
        const docData = await addDoc(collectionRef, {
            ...toFirestore(faculty),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return docData.id;
    }

    async update(instituteId: string, facultyId: string, value: any): Promise<boolean> {
        // console.log(`Updating: institutes/${instituteId}/faculties/${facultyId}`);
        // console.log(value);
        const collectionRef = collection(firestore, facultyCollectionName.replace("{0}", instituteId));

        const docRef = doc(collectionRef, facultyId);

        if (docRef == null) {
            console.error(`Error updating: institutes/${instituteId}/faculties/${facultyId}`);
            console.error("Doesn't find Faculty");
            return false;
        }

        try {
            // Clone value object
            const newValue = { ...value };
            // Set updatedAt field with Server timestamp
            newValue.updatedAt = serverTimestamp();

            // Clean undefined values recursively so updateDoc never throws Unsupported field value: undefined
            const cleanData = (obj: any): any => {
                if (obj === null || typeof obj !== "object") return obj;
                if (obj instanceof Date || obj instanceof Timestamp || ('_methodName' in obj)) return obj;
                if (Array.isArray(obj)) return obj.map(cleanData);
                const result: any = {};
                Object.keys(obj).forEach((key) => {
                    if (obj[key] !== undefined) {
                        result[key] = cleanData(obj[key]);
                    }
                });
                return result;
            };

            const dataToUpdate = cleanData(toFirestore(newValue));

            await updateDoc(docRef, dataToUpdate);
            return true;

        } catch (error) {
            console.error(`Error updating: institutes/${instituteId}/faculties/${facultyId}`);
            console.error(value);
            console.error(error);
            return false;
        }
    }

    async delete(instituteId: string, facultyId: string): Promise<boolean> {
        const collectionRef = collection(firestore, facultyCollectionName.replace("{0}", instituteId));

        const docRef = doc(collectionRef, facultyId);

        if (docRef.id == null) {
            console.error(`Error deleting: institutes/${instituteId}/faculties/${facultyId}`);
            console.error("Doesn't find Faculty");
            return false;
        }

        try {
            // Delete all careers belonging to this faculty first
            const careersCollectionRef = collection(
                firestore,
                `institutes/${instituteId}/faculties/${facultyId}/careers`
            );
            const careersSnapshot = await getDocs(careersCollectionRef);

            if (!careersSnapshot.empty) {
                const BATCH_SIZE = 400;
                const docs = careersSnapshot.docs;
                for (let i = 0; i < docs.length; i += BATCH_SIZE) {
                    const batch = writeBatch(firestore);
                    const chunk = docs.slice(i, i + BATCH_SIZE);
                    for (const careerDoc of chunk) {
                        batch.delete(careerDoc.ref);
                    }
                    await batch.commit();
                }
            }

            await deleteDoc(docRef);
            return true;
        } catch (error) {
            console.error(`Error deleting: institutes/${instituteId}/faculties/${facultyId}`);
            console.error(error);
            return false;
        }
    }

    async setActive(instituteId: string, facultyId: string, active: boolean): Promise<boolean> {
        const collectionRef = collection(firestore, facultyCollectionName.replace("{0}", instituteId));

        const docRef = doc(collectionRef, facultyId);

        if (docRef == null) {
            console.error(`Error setting active: institutes/${instituteId}/faculties/${facultyId}`);
            console.error("Doesn't find Faculty");
            return false;
        }

        try {
            await updateDoc(docRef, {
                active: active,
                updatedAt: serverTimestamp()
            });// Add a new document with a generated id.
            return true;

        } catch (error) {
            console.error(`Error setting active: institutes/${instituteId}/faculties/${facultyId}`);
            console.error(`active value: ${active}`);
            console.error(error);
            return false;
        }
    }
}

export default new FacultyService();
